import React, { useState, useEffect } from 'react'
import { PENDING_MESSAGE_ID } from 'core/constants'
import { isNotSentId } from 'core/helpers'
import { useFeatures, useTranslations } from 'core/hooks'
import { Time } from 'components/misc'
import { ccpProvider } from 'core/CcpProvider'
import { getGiftPrice } from 'core/giftPrices'
import CONFIG from 'core/globals'
import { MessageBasic } from './MessageDefault'
import UpgradeLink from '../UpgradeLink'

/**
 * A gift an AP sent that the recipient pays to reveal. ccp-api stores it hidden and unhides it when
 * the credits are taken, so `message.hidden` is the source of truth and survives a reload.
 *
 * While locked, the real gift is not rendered at all - only the mystery box every site shares from
 * its common bucket. The gift url stays out of the DOM until it has been paid for.
 */
const MessageGiftReveal = ({ message, isCorrespondent, users, conversationUid }) => {
  const [isLocked, setIsLocked] = useState(!isCorrespondent && Boolean(message.hidden))
  const [loading, setLoading] = useState(false)
  const [price, setPrice] = useState(null)

  let { isCreditSite } = useFeatures()
  if (isCreditSite === 0) { isCreditSite = false }
  const { creditsPerGiftReveal } = useTranslations()

  useEffect(() => {
    setIsLocked(!isCorrespondent && Boolean(message.hidden))
  }, [message, isCorrespondent])

  useEffect(() => {
    let cancelled = false
    getGiftPrice(message.message)
      .then(giftPrice => { if (!cancelled) { setPrice(giftPrice) } })
      .catch(() => { /* the cta falls back to its priceless wording */ })

    return () => { cancelled = true }
  }, [message.message])

  const props = {
    time: <Time since={message.sentAt}/>,
    user: isCorrespondent ? users.me : users.other,
    sent: isCorrespondent,
    pending: message.id === PENDING_MESSAGE_ID,
    failed: isNotSentId(message.id),
    shouldShowTimeStamp: message.shouldShowTimeStamp,
  }

  /**
   * `hidden` is not observable, so the local state is what re-renders. The model is updated in step so
   * that a re-render from the same instance does not show the gift as locked again; if the list is
   * refetched instead, the server already reports it unhidden. Kept synchronous and out of reveal() so
   * the read and the write cannot straddle an await.
   */
  const markRevealed = () => {
    message.hidden = false
    setIsLocked(false)
  }

  const reveal = async e => {
    e.preventDefault()
    if (loading) { return }
    setLoading(true)

    const [, err] = await ccpProvider.api.user.revealGift({ messageId: message.id })
    if (!err) {
      markRevealed()
      CONFIG.hooks.onRevealMessage({ messageId: message.id, conversationUid })
    } else if (err.statusCode === 402) {
      CONFIG.hooks.onMessageSend({ showPurchaseModal: true })
    }

    setLoading(false)
  }

  // The action is uppercase by design, the cost stays lowercase. A site can override the whole
  // wording through the creditsPerGiftReveal translation ({credits} is replaced with the price).
  const ctaContent = creditsPerGiftReveal
    ? <span dangerouslySetInnerHTML={{ __html: String(creditsPerGiftReveal).replace('{credits}', price ?? '') }}/>
    : <span>REVEAL GIFT{price ? <span className="gift-unlock-cta__credits"> ({price} credits)</span> : null}</span>

  if (!isLocked) {
    return (
      <MessageBasic {...props} type="gift">
        <img src={message.message} className="chat-img gift-reveal__image" alt="gift"/>
      </MessageBasic>
    )
  }

  return (
    <MessageBasic {...props} type="gift">
      <div className="gift-reveal gift-reveal--locked">
        <img src={`https://common.${CONFIG.siteDomain}/images/gift.png`} className="gift-reveal__mystery" alt=""/>
      </div>
      {isCreditSite ? (
        <a
          href="#"
          onClick={loading ? null : reveal}
          className={`gift-unlock-cta${loading ? ' gift-unlock-cta--loading' : ''}`}
        >{ctaContent}</a>
      ) : (
        <UpgradeLink className="gift-unlock-cta">{ctaContent}</UpgradeLink>
      )}
    </MessageBasic>
  )
}

export default MessageGiftReveal
